<template>
  <div :class="{ 'w-full': fullWidth }">
    <label
      :class="labelClasses"
      :for="id"
    >
      <span
        :class="{ 'sr-only': hideLabel }"
        v-text="label"
      />
      <span
        v-if="required"
        :class="{ 'sr-only': hideLabel }"
        aria-hidden="true"
        class="ml-1 text-red"
        v-text="'*'"
      />
      <!-- The space is used in lieu of left margin in order to ensure the label is announced properly by a screen reader. -->
      <span
        v-else
        class="font-normal"
        v-text="' (Optional)'"
      />
      <slot name="inputLink" />
    </label>
    <div class="relative">
      <input
        :id="id"
        v-model="value"
        :aria-describedby="helperTextId"
        :aria-invalid="!!errorMessage"
        :autocomplete="autocomplete"
        :class="[inputClasses, { 'pr-10': hasSlotContent($slots.icon), 'border-red': showErrorMessage && errorMessage }]"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :type="type"
        class="body-sm block w-full"
        v-bind="$attrs"
      >
      <template v-if="hasSlotContent($slots.icon)">
        <div class="absolute inset-y-0 right-0 flex items-center pr-4">
          <slot name="icon" />
        </div>
      </template>
    </div>
    <div
      v-if="showErrorMessage && errorMessage"
      class="body-sm mt-1 flex items-center gap-2 text-red"
    >
      <IconExclamationCircleFilled
        :size="20"
        aria-hidden="true"
        class="text-red"
      />
      <span v-text="errorMessage" />
    </div>
    <div
      v-if="hasSlotContent($slots.helperText)"
      :id="helperTextId"
      class="body-sm mt-1"
    >
      <slot name="helperText" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useField } from 'vee-validate'
import { IconExclamationCircleFilled } from '@tabler/icons-vue'

import '@js/utils/validators'
import { HtmlAutocomplete } from '@js/types/HtmlAutocomplete'
import { useHasSlotContent } from '@js/composables/useHasSlotContent'

defineOptions({
  name: 'BaseTextInput',
})

const props = withDefaults(
  defineProps<{
    id: string
    name: string
    label: string
    disabled?: boolean
    required?: boolean
    fullWidth?: boolean
    hideLabel?: boolean
    placeholder?: string
    helperTextId?: string
    validationRules?: string
    showErrorMessage?: boolean
    labelClasses?: string | null
    autocomplete?: HtmlAutocomplete
    inputClasses?: string | string[] | null
    type?: 'text' | 'password' | 'email' | 'number' | 'date' | 'tel'
  }>(),
  {
    type: 'text',
    disabled: false,
    fullWidth: true,
    placeholder: '',
    required: false,
    helperTextId: '',
    hideLabel: false,
    inputClasses: null,
    labelClasses: null,
    autocomplete: 'off',
    validationRules: '',
    showErrorMessage: true,
  },
)

defineModel<string | number>({
  default: '',
})

const { hasSlotContent } = useHasSlotContent()

const { errorMessage, value } = useField(
  () => props.name,
  props.validationRules,
  {
    syncVModel: true,
  },
)
</script>
